import { useCursor } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { useKeydown } from "@tosize/ui";
import { Fragment, useEffect, useMemo } from "react";
import { match } from "ts-pattern";
import { useConfig } from "../../../context/ConfigContext";
import { useCabinetStore } from "../../../context/StoreContext";
import { getDimensions } from "../../../hooks/getDimensions";
import { CompartmentModus, LayoutModus, useCanvasStore } from "../../../hooks/useCanvasStore";
import { canChangeBackgroundDepth } from "../data";
import { BACKGROUND_THICKNESS } from "../data/Background";
import {
	type CompartmentType,
	type Side,
	compartmentsOnSameX,
	compartmentsOnSameY,
	isNextToCompartmentOnX,
	isNextToCompartmentOnY,
	touchesEdges,
} from "../data/Compartment";
import useCompartment from "../hooks/useCompartment";
import SizePill from "../utils/SizePill";
import { Merger } from "./Compartment/Merger";
import OverlayMaterial from "./Compartment/OverlayMaterial";

interface Props {
	compartment: CompartmentType;
}

export function Compartment({ compartment }: Props) {
	const { invalidate } = useThree();
	const { mode } = useConfig();

	const {
		layoutMode,
		compartmentMode,
		showDimensions,
		selectedCompartmentIds,
		fullScreen,
		selectCompartment,
		deselectCompartment,
		setHoveredCompartmentId,
	} = useCanvasStore();

	const {
		state,
		splitVertically,
		splitHorizontally,
		addShelf,
		removeShelf,
		addDoor,
		removeDoor,
		addDoubleDoor,
	} = useCabinetStore();

	const { canApplyCurrentMode, isHovered, isSelected } = useCompartment(compartment);

	const dimensions = getDimensions(compartment);

	const availableEdges = Object.entries(touchesEdges(compartment, state))
		.filter(([, touches]) => !touches)
		.map(([side]) => side as Side);

	const isAvailableToDistribute = useMemo(() => {
		const compartments = selectedCompartmentIds.map((id) => state.compartments.get(id)!);

		const isLeftOrRight = compartments
			.filter((c) => compartmentsOnSameY(c, compartment))
			.some((c) => isNextToCompartmentOnX(c, compartment, state.thickness));

		const isTopOrBottom = compartments
			.filter((c) => compartmentsOnSameX(c, compartment))
			.some((c) => isNextToCompartmentOnY(c, compartment, state.thickness));

		if (layoutMode === LayoutModus.distributeHorizontal) {
			return isLeftOrRight && !isTopOrBottom;
		}

		if (layoutMode === LayoutModus.distributeVertical) {
			return !isLeftOrRight && isTopOrBottom;
		}

		return false;
	}, [layoutMode, compartment, selectedCompartmentIds, state.compartments, state.thickness]);

	function handleCompartmentClick() {
		if (isSelected) {
			return deselectCompartment(compartment);
		}

		const canSelect = match(layoutMode)
			.with(
				LayoutModus.distributeHorizontal,
				LayoutModus.distributeVertical,
				() => isAvailableToDistribute || selectedCompartmentIds.length === 0,
			)
			.with(LayoutModus.background, () => canChangeBackgroundDepth(compartment))
			.otherwise(() => false);

		if (canSelect) {
			return selectCompartment(compartment);
		}

		if (canApplyCurrentMode) {
			if (layoutMode) {
				return match(layoutMode)
					.with(LayoutModus.addHorizontal, () => splitHorizontally(compartment))
					.with(LayoutModus.addVertical, () => splitVertically(compartment))
					.run();
			}

			if (compartmentMode) {
				return match(compartmentMode)
					.with(CompartmentModus.DoorLeft, () => addDoor(compartment, "left"))
					.with(CompartmentModus.DoorRight, () => addDoor(compartment, "right"))
					.with(CompartmentModus.DoorDouble, () => addDoubleDoor(compartment))
					.with(CompartmentModus.RemoveDoor, () => removeDoor(compartment))
					.with(CompartmentModus.AddShelf, () => addShelf(compartment))
					.with(CompartmentModus.RemoveShelf, () => removeShelf(compartment))
					.exhaustive();
			}
		}
	}

	useKeydown(() => deselectCompartment(compartment));

	useCursor(!!canApplyCurrentMode && isHovered, "pointer");

	useCursor(!canApplyCurrentMode && isHovered, "not-allowed");

	useEffect(() => {
		showDimensions && invalidate();
	}, [invalidate, showDimensions]);

	return (
		<Fragment>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: no need for key event */}
			<mesh
				userData={{ id: compartment.id }}
				position={[dimensions.x, dimensions.y, dimensions.z]}
				onClick={(evt) => {
					evt.stopPropagation();

					console.info("Compartment", compartment);

					handleCompartmentClick();
				}}
				onPointerOver={(evt) => {
					evt.stopPropagation();

					setHoveredCompartmentId(compartment.id);
				}}
				onPointerLeave={() => {
					setHoveredCompartmentId(null);
				}}
			>
				<boxGeometry args={[dimensions.width, dimensions.height, dimensions.depth + 0.001]} />

				<OverlayMaterial compartment={compartment} />

				{(layoutMode === LayoutModus.background || showDimensions) && (
					<SizePill
						variant="depth"
						size={
							state.depth -
							BACKGROUND_THICKNESS -
							(compartment.background?.z ?? 0) -
							(compartment.door?.type === "single"
								? compartment.door.height
								: (compartment.door?.left.height ?? compartment.door?.right.height ?? 0))
						}
						position={
							fullScreen
								? { x: 0, y: 0, z: dimensions.depth / 2 }
								: {
										x: dimensions.width / 2 - 0.14,
										y: dimensions.height / 2 - 0.06,
										z: dimensions.depth / 2,
									}
						}
					/>
				)}

				{mode !== "view" && (isHovered || showDimensions) && (
					<Fragment>
						<SizePill
							variant="height"
							size={compartment.height}
							position={{
								x: dimensions.width / -2 + (fullScreen ? (dimensions.depth / 20) * -1 : 0.14),
								y: 0,
								z: dimensions.depth / 2,
							}}
						/>
						<SizePill
							variant="width"
							size={compartment.width}
							position={{
								x: 0,
								y: dimensions.height / -2 + (fullScreen ? (dimensions.depth / 20) * -1 : 0.06),
								z: dimensions.depth / 2,
							}}
						/>
					</Fragment>
				)}
			</mesh>

			{!fullScreen &&
				mode !== "view" &&
				mode !== "expandedView" &&
				layoutMode === LayoutModus.merge && (
					<Fragment>
						{availableEdges.map((side) => (
							<Merger key={side} side={side} compartment={compartment} />
						))}
					</Fragment>
				)}
		</Fragment>
	);
}
